// Katalog písem nabízených v administraci vzhledu (skupiny: základní, bezpatková, patkové, slab, zaoblené,
// úzká, široká, neproporcionální — aby se písma v nabídce opravdu lišila). Klíče musí odpovídat
// App\Services\ThemeService::FONTS (backend vrací povolené klíče přes ThemeTL::getThemeOptions()).
// Písma se načítají lokálně (@fontsource, bez volání třetích stran) a leniwě — až když je některé
// téma opravdu použije.

/** Skupina písma v nabídce editoru (viz `theme.fontGroup.*` v překladech). */
export type ThemeFontGroup = 'basic' | 'sans' | 'serif' | 'slab' | 'rounded' | 'condensed' | 'wide' | 'mono';

export type ThemeFont = {
	key: string;
	label: string;
	/** hodnota pro `font-family` */
	css: string;
	group: ThemeFontGroup;
	load?: () => Promise<unknown>;
};

/** Pořadí skupin v nabídce. */
export const themeFontGroups: ThemeFontGroup[] = ['basic', 'sans', 'serif', 'slab', 'rounded', 'condensed', 'wide', 'mono'];

export const themeFonts: ThemeFont[] = [
	{ key: 'system', label: 'Systémové', css: 'system-ui, sans-serif', group: 'basic' },
	{ key: 'georgia', label: 'Georgia', css: 'Georgia, "Times New Roman", Times, serif', group: 'basic' },

	{
		key: 'inter', label: 'Inter', css: '"Inter Variable", system-ui, sans-serif', group: 'sans',
		load: () => import('@fontsource-variable/inter'),
	},
	{
		key: 'roboto', label: 'Roboto', css: 'Roboto, system-ui, sans-serif', group: 'sans',
		load: () => Promise.all([import('@fontsource/roboto/400.css'), import('@fontsource/roboto/700.css')]),
	},
	{
		key: 'source-sans-3', label: 'Source Sans 3', css: '"Source Sans 3 Variable", system-ui, sans-serif', group: 'sans',
		load: () => import('@fontsource-variable/source-sans-3'),
	},

	{
		key: 'merriweather', label: 'Merriweather', css: '"Merriweather Variable", Georgia, serif', group: 'serif',
		load: () => import('@fontsource-variable/merriweather'),
	},
	{
		key: 'playfair-display', label: 'Playfair Display', css: '"Playfair Display Variable", Georgia, serif', group: 'serif',
		load: () => import('@fontsource-variable/playfair-display'),
	},
	{
		key: 'roboto-slab', label: 'Roboto Slab', css: '"Roboto Slab Variable", Georgia, serif', group: 'slab',
		load: () => import('@fontsource-variable/roboto-slab'),
	},

	{
		key: 'nunito', label: 'Nunito', css: '"Nunito Variable", system-ui, sans-serif', group: 'rounded',
		load: () => import('@fontsource-variable/nunito'),
	},

	{
		key: 'roboto-condensed', label: 'Roboto Condensed', css: '"Roboto Condensed Variable", "Arial Narrow", sans-serif', group: 'condensed',
		load: () => import('@fontsource-variable/roboto-condensed'),
	},
	{
		key: 'oswald', label: 'Oswald', css: '"Oswald Variable", "Arial Narrow", sans-serif', group: 'condensed',
		load: () => import('@fontsource-variable/oswald'),
	},

	{
		key: 'montserrat', label: 'Montserrat', css: '"Montserrat Variable", system-ui, sans-serif', group: 'wide',
		load: () => import('@fontsource-variable/montserrat'),
	},
	{
		key: 'lexend', label: 'Lexend', css: '"Lexend Variable", system-ui, sans-serif', group: 'wide',
		load: () => import('@fontsource-variable/lexend'),
	},
	{
		key: 'unbounded', label: 'Unbounded', css: '"Unbounded Variable", system-ui, sans-serif', group: 'wide',
		load: () => import('@fontsource-variable/unbounded'),
	},

	{
		key: 'jetbrains-mono', label: 'JetBrains Mono', css: '"JetBrains Mono Variable", ui-monospace, monospace', group: 'mono',
		load: () => import('@fontsource-variable/jetbrains-mono'),
	},
	{
		key: 'ibm-plex-mono', label: 'IBM Plex Mono', css: '"IBM Plex Mono", ui-monospace, monospace', group: 'mono',
		load: () => Promise.all([import('@fontsource/ibm-plex-mono/400.css'), import('@fontsource/ibm-plex-mono/700.css')]),
	},
];

export const findThemeFont = (key: string): ThemeFont => themeFonts.find(f => f.key === key) ?? themeFonts[0];

const loaded = new Set<string>();

/** Načte soubory písma (jednou). Chyby se ignorují — vykreslí se záložní písmo z `css`. */
export const loadThemeFont = async (key: string): Promise<void> => {
	const font = findThemeFont(key);
	if (!font.load || loaded.has(font.key)) return;
	loaded.add(font.key);
	try {
		await font.load();
	} catch {
		loaded.delete(font.key);
	}
};
